import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Logo from '../../../components/Logo';

const DashboardPreview = () => {
  const [activeMetric, setActiveMetric] = useState('sales');
  const [liveData, setLiveData] = useState({
    todaySales: 2847,
    activeOrders: 23,
    completedOrders: 156,
    avgOrderValue: 47,
    customerSatisfaction: 4.8,
    inventoryAlerts: 3
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveData(prev => ({
        ...prev,
        todaySales: prev.todaySales + Math.floor(Math.random() * 50),
        activeOrders: Math.max(15, prev.activeOrders + Math.floor(Math.random() * 6) - 3),
        completedOrders: prev.completedOrders + Math.floor(Math.random() * 3)
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const metrics = [
    {
      id: 'sales',
      title: 'Günlük Satış',
      value: `₺${liveData.todaySales.toLocaleString()}`,
      change: '+12.5%',
      icon: 'TrendingUp',
      color: 'bg-green-500'
    },
    {
      id: 'orders',
      title: 'Aktif Siparişler',
      value: liveData.activeOrders,
      change: '+5',
      icon: 'Clock',
      color: 'bg-blue-500'
    },
    {
      id: 'completed',
      title: 'Tamamlanan',
      value: liveData.completedOrders,
      change: '+23',
      icon: 'CheckCircle',
      color: 'bg-purple-500'
    },
    {
      id: 'avg',
      title: 'Ortalama Sipariş',
      value: `₺${liveData.avgOrderValue}`,
      change: '+8.2%',
      icon: 'DollarSign',
      color: 'bg-orange-500'
    }
  ];

  const recentOrders = [
    { id: '#1247', customer: 'Mehmet K.', items: 'Americano + Cheesecake', amount: '₺45', status: 'preparing', time: '2 dk' },
    { id: '#1248', customer: 'Ayşe D.', items: 'Cappuccino + Croissant', amount: '₺38', status: 'ready', time: '5 dk' },
    { id: '#1249', customer: 'Can Y.', items: 'Latte + Muffin', amount: '₺42', status: 'new', time: 'Yeni' },
    { id: '#1250', customer: 'Zeynep A.', items: 'Espresso + Tiramisu', amount: '₺52', status: 'preparing', time: '1 dk' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-600';
      case 'preparing': return 'bg-yellow-100 text-yellow-600';
      case 'ready': return 'bg-green-100 text-green-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new': return 'Yeni';
      case 'preparing': return 'Hazırlanıyor';
      case 'ready': return 'Hazır';
      default: return 'Bilinmiyor';
    }
  };

  return (
    <section id="dashboard-preview" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Icon name="BarChart3" size={16} />
            <span className="font-inter font-accent-medium text-sm">
              Canlı Dashboard
            </span>
          </div>
          
          <h2 className="font-poppins font-heading-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
            Gerçek Zamanlı{' '}
            <span className="text-primary">Kontrol Paneli</span>
          </h2>
          
          <p className="font-open-sans font-body-normal text-xl text-text-secondary max-w-3xl mx-auto">
            İşletmenizin tüm verilerini tek ekranda görün, anlık kararlar alın
          </p>
        </div>

        {/* Dashboard Container */}
        <div className="bg-gray-50 rounded-3xl p-8 lg:p-12">
          {/* Dashboard Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              {/* Kırmızı dolgu simgenin KENDİSİNDE; ayrı bir kırmızı kutuya
                  konsaydı kırmızı işaret kırmızı zeminde görünmezdi. */}
              <Logo variant="icon" size={48} className="rounded-xl" />
              <div>
                <h3 className="font-poppins font-heading-bold text-2xl text-foreground">
                  Özkan Cafe Dashboard
                </h3>
                <p className="font-open-sans font-body-normal text-text-secondary">
                  Bugün, {new Date().toLocaleDateString('tr-TR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-inter font-accent-medium text-sm text-text-secondary">
                Canlı Veri
              </span>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metrics.map((metric) => (
              <button
                key={metric.id}
                onClick={() => setActiveMetric(metric.id)}
                className={`bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 text-left ${
                  activeMetric === metric.id ? 'ring-2 ring-primary transform scale-105' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${metric.color} rounded-xl flex items-center justify-center`}>
                    <Icon name={metric.icon} size={24} color="white" />
                  </div>
                  <span className="text-green-600 font-inter font-accent-semibold text-sm">
                    {metric.change}
                  </span>
                </div>
                
                <div>
                  <p className="font-poppins font-heading-bold text-2xl text-foreground mb-1">
                    {metric.value}
                  </p>
                  <p className="font-open-sans font-body-normal text-sm text-text-secondary">
                    {metric.title}
                  </p>
                </div>
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Recent Orders */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h4 className="font-poppins font-heading-semibold text-xl text-foreground">
                  Son Siparişler
                </h4>
                <button className="text-primary hover:text-primary/80 transition-colors duration-300">
                  <Icon name="MoreHorizontal" size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                {recentOrders.map((order, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-inter font-accent-semibold text-sm text-foreground">
                          {order.id}
                        </span>
                        <span className="font-open-sans font-body-normal text-xs text-text-secondary">
                          {order.customer}
                        </span>
                        <span className="font-open-sans font-body-normal text-xs text-text-secondary">
                          • {order.time}
                        </span>
                      </div>
                      <p className="font-open-sans font-body-normal text-sm text-text-secondary">
                        {order.items}
                      </p>
                    </div>
                    
                    <div className="text-right">
                      <p className="font-poppins font-heading-semibold text-lg text-foreground mb-2">
                        {order.amount}
                      </p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-inter font-accent-medium ${getStatusColor(order.status)}`}>
                        {getStatusText(order.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <button className="w-full mt-4 py-3 text-primary hover:bg-primary/5 rounded-xl transition-colors duration-300 font-inter font-accent-medium text-sm">
                Tüm Siparişleri Görüntüle
              </button>
            </div>

            {/* Quick Actions & Analytics */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h4 className="font-poppins font-heading-semibold text-xl text-foreground mb-6">
                  Hızlı İşlemler
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <button className="flex items-center space-x-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors duration-300">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <Icon name="Plus" size={20} color="white" />
                    </div>
                    <div className="text-left">
                      <p className="font-inter font-accent-semibold text-sm text-foreground">
                        Yeni Sipariş
                      </p>
                      <p className="font-open-sans font-body-normal text-xs text-text-secondary">
                        Manuel ekle
                      </p>
                    </div>
                  </button>
                  
                  <button className="flex items-center space-x-3 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors duration-300">
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                      <Icon name="Package" size={20} color="white" />
                    </div>
                    <div className="text-left">
                      <p className="font-inter font-accent-semibold text-sm text-foreground">
                        Stok Güncelle
                      </p>
                      <p className="font-open-sans font-body-normal text-xs text-text-secondary">
                        Envanter yönet
                      </p>
                    </div>
                  </button>
                  
                  <button className="flex items-center space-x-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors duration-300">
                    <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                      <Icon name="BarChart3" size={20} color="white" />
                    </div>
                    <div className="text-left">
                      <p className="font-inter font-accent-semibold text-sm text-foreground">
                        Raporlar
                      </p>
                      <p className="font-open-sans font-body-normal text-xs text-text-secondary">
                        Analiz görüntüle
                      </p>
                    </div>
                  </button>
                  
                  <button className="flex items-center space-x-3 p-4 bg-orange-50 hover:bg-orange-100 rounded-xl transition-colors duration-300">
                    <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                      <Icon name="Settings" size={20} color="white" />
                    </div>
                    <div className="text-left">
                      <p className="font-inter font-accent-semibold text-sm text-foreground">
                        Ayarlar
                      </p>
                      <p className="font-open-sans font-body-normal text-xs text-text-secondary">
                        Sistem yapılandır
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Alerts */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-poppins font-heading-semibold text-lg text-foreground">
                    Uyarılar
                  </h4>
                  <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full font-inter font-accent-semibold text-xs">
                    {liveData.inventoryAlerts}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg">
                    <Icon name="AlertTriangle" size={16} className="text-yellow-600" />
                    <div className="flex-1">
                      <p className="font-inter font-accent-medium text-sm text-foreground">
                        Süt stoku azalıyor
                      </p>
                      <p className="font-open-sans font-body-normal text-xs text-text-secondary">
                        2 gün kaldı
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                    <Icon name="AlertCircle" size={16} className="text-red-600" />
                    <div className="flex-1">
                      <p className="font-inter font-accent-medium text-sm text-foreground">
                        Kahve çekirdeği bitti
                      </p>
                      <p className="font-open-sans font-body-normal text-xs text-text-secondary">
                        Acil sipariş gerekli
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                    <Icon name="Info" size={16} className="text-blue-600" />
                    <div className="flex-1">
                      <p className="font-inter font-accent-medium text-sm text-foreground">
                        Yeni güncelleme mevcut
                      </p>
                      <p className="font-open-sans font-body-normal text-xs text-text-secondary">
                        v2.1.4 sürümü
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white">
            <h3 className="font-poppins font-heading-bold text-2xl mb-4">
              Bu Dashboard'u Kendi İşletmenizde Görün
            </h3>
            <p className="font-open-sans font-body-normal text-lg opacity-90 mb-6 max-w-2xl mx-auto">
              Gerçek verilerinizle çalışan canlı dashboard'u ücretsiz demo ile deneyimleyin
            </p>
            
            <button
              onClick={() => window.open('https://panel.cafeotomasyonu.com', '_blank')}
              className="inline-flex items-center space-x-2 bg-white text-primary px-8 py-4 rounded-xl font-poppins font-cta-semibold hover:bg-gray-100 transition-colors duration-300 shadow-lg"
            >
              <Icon name="Play" size={20} />
              <span>Canlı Demo İzle</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardPreview;